import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Plus, PencilSimple, Trash, DownloadSimple, MagnifyingGlass, FileText, FilePdf, CheckCircle, Copy } from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { differenceInDays, parseISO, format } from 'date-fns';
import { useAuth } from '@/context/AuthContext';
import { DateFilterBar } from './tracker/DateFilterBar';
import { MonthBar } from './tracker/MonthBar';
import { MonthSummary, RecruiterMonthTable } from './tracker/MonthSummary';
import { RecruitersDialog } from './tracker/RecruitersDialog';
import { CandidateRow } from './tracker/CandidateRow';
import { CompanyPicker } from './tracker/CompanyPicker';
import { DuplicatesDialog, useDuplicateScan } from './tracker/DuplicatesDialog';
import { findDuplicate } from './tracker/helpers';
import { logError, logWarn } from '@/lib/logger';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Tracker = () => {
  const { isOwner, canSeeFinance } = useAuth();
  const [candidates, setCandidates] = useState([]);
  const [filteredCandidates, setFilteredCandidates] = useState([]);
  const [recruiters, setRecruiters] = useState([]);
  const [companyNames, setCompanyNames] = useState([]);
  const [isRecruitersOpen, setIsRecruitersOpen] = useState(false);
  const [newRecruiter, setNewRecruiter] = useState('');
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilterField, setDateFilterField] = useState('interview_date'); // 'interview_date' | 'date_of_joining'
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const currentMonth = format(new Date(), 'yyyy-MM');
  const [month, setMonth] = useState(currentMonth); // '' = all months
  const [monthView, setMonthView] = useState('both'); // 'both' | 'interview_date' | 'date_of_joining'
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [isDupOpen, setIsDupOpen] = useState(false);
  const dupScan = useDuplicateScan();
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [currentCandidate, setCurrentCandidate] = useState(null);
  const [formData, setFormData] = useState({
    interview_date: '',
    candidate_name: '',
    candidate_number: '',
    company_name: '',
    date_of_joining: '',
    recruiter_name: '',
    joining_status: 'Pending',
    job_category: '',
    salary: '',
    consultancy_charge: '',
    charge_override: '',
    override_reason: ''
  });

  const fetchCandidates = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/candidates`);
      setCandidates(response.data);
      setFilteredCandidates(response.data);
    } catch (error) {
      logError('Error fetching candidates:', error);
      toast.error('Failed to fetch candidates');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchRecruiters = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/recruiters`);
      setRecruiters(response.data);
    } catch (error) {
      logError('Error fetching recruiters:', error);
    }
  }, []);

  const fetchCompanyNames = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/companies/names`);
      setCompanyNames(response.data || []);
    } catch (error) {
      logError('Error fetching company names:', error);
    }
  }, []);

  useEffect(() => {
    fetchCandidates();
    fetchRecruiters();
    fetchCompanyNames();
    if (isOwner) dupScan.run();
  }, [fetchCandidates, fetchRecruiters, fetchCompanyNames, isOwner, dupScan.run]);

  const handleAddRecruiter = async (e) => {
    e.preventDefault();
    const name = newRecruiter.trim();
    if (!name) return;
    try {
      await axios.post(`${API}/recruiters`, { name });
      toast.success(`Recruiter "${name}" added`);
      setNewRecruiter('');
      fetchRecruiters();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add recruiter');
    }
  };

  const handleDeleteRecruiter = async (rec) => {
    if (!window.confirm(`Remove recruiter "${rec.name}"?`)) return;
    try {
      await axios.delete(`${API}/recruiters/${rec.id}`);
      toast.success(`Recruiter "${rec.name}" removed`);
      fetchRecruiters();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove recruiter');
    }
  };

  const handleSendAgreement = async (candidate) => {
    try {
      const { data } = await axios.get(`${API}/candidates/${candidate.id}/agreement`);
      if (!data.whatsapp_url) {
        toast.error('Missing phone number for this candidate');
        return;
      }
      window.open(data.whatsapp_url, '_blank', 'noopener,noreferrer');
      // Mark as sent (fire-and-forget; log failure but don't block UX)
      try {
        await axios.post(`${API}/candidates/${candidate.id}/agreement/sent`);
        fetchCandidates();
      } catch (markErr) {
        logWarn('Failed to mark agreement as sent:', markErr?.response?.data?.detail || markErr.message);
      }
      toast.success(`Agreement opened for ${candidate.candidate_name}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate agreement');
    }
  };

  const handlePreviewAgreementPdf = async (candidate) => {
    try {
      const res = await axios.get(`${API}/candidates/${candidate.id}/agreement.pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const win = window.open(url, '_blank', 'noopener,noreferrer');
      if (!win) {
        toast.error('Pop-up blocked — allow pop-ups to preview');
      } else {
        toast.success('Preview opened in new tab');
      }
      // Release the blob URL once the new tab has had time to load
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (err) {
      toast.error('Failed to preview PDF');
    }
  };

  const handleDownloadAgreementPdf = async (candidate) => {
    try {
      const res = await axios.get(`${API}/candidates/${candidate.id}/agreement.pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `TulsiJobs_Agreement_${candidate.candidate_name.replace(/\s+/g, '_')}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      // Also mark as sent — downloading implies the owner is sending it
      try {
        await axios.post(`${API}/candidates/${candidate.id}/agreement/sent`);
        fetchCandidates();
      } catch (markErr) {
        logWarn('Failed to mark agreement as sent:', markErr?.response?.data?.detail || markErr.message);
      }
      toast.success('Agreement PDF downloaded');
    } catch (err) {
      toast.error('Failed to download PDF');
    }
  };

  useEffect(() => {
    let filtered = candidates;
    if (month) {
      const inMonth = (d) => !!d && d.slice(0, 7) === month;
      filtered = filtered.filter(c => monthView === 'both'
        ? inMonth(c.interview_date) || inMonth(c.date_of_joining)
        : inMonth(c[monthView]));
    }
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      filtered = filtered.filter(c =>
        c.candidate_name.toLowerCase().includes(q) ||
        c.company_name.toLowerCase().includes(q) ||
        (c.recruiter_name || '').toLowerCase().includes(q)
      );
    }
    if (dateFrom || dateTo) {
      filtered = filtered.filter(c => {
        const d = c[dateFilterField];
        if (!d) return false;
        if (dateFrom && d < dateFrom) return false;
        if (dateTo && d > dateTo) return false;
        return true;
      });
    }
    setFilteredCandidates(filtered);
  }, [searchTerm, candidates, dateFrom, dateTo, dateFilterField, month, monthView]);

  const clearDateFilter = () => { setDateFrom(''); setDateTo(''); };

  const handleMarkJoined = async (candidate) => {
    if (candidate.joining_status === 'Joined') return;
    try {
      const payload = {
        interview_date: candidate.interview_date,
        candidate_name: candidate.candidate_name,
        candidate_number: candidate.candidate_number,
        company_name: candidate.company_name,
        date_of_joining: candidate.date_of_joining,
        recruiter_name: candidate.recruiter_name || '',
        job_category: candidate.job_category || '',
        salary: candidate.salary || 0,
        joining_status: 'Joined',
      };
      if (canSeeFinance) {
        payload.consultancy_charge = candidate.consultancy_charge != null ? candidate.consultancy_charge : 0;
      }
      await axios.put(`${API}/candidates/${candidate.id}`, payload);
      toast.success(`${candidate.candidate_name} marked as Joined`);
      fetchCandidates();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to mark as Joined');
    }
  };

  // Inline-edit: PATCH-style PUT with merged payload. Optimistically update UI, rollback on error.
  const inlineSave = useCallback(async (candidate, field, newValue) => {
    const original = candidate[field];
    if (original === newValue || (original == null && newValue === '')) return; // no change
    // Optimistic update
    setCandidates(prev => prev.map(c => c.id === candidate.id ? { ...c, [field]: newValue } : c));
    try {
      // Build full payload with the single field changed
      const payload = {
        interview_date: candidate.interview_date,
        candidate_name: candidate.candidate_name,
        candidate_number: candidate.candidate_number,
        company_name: candidate.company_name,
        date_of_joining: candidate.date_of_joining,
        recruiter_name: candidate.recruiter_name || '',
        job_category: candidate.job_category || '',
        salary: candidate.salary || 0,
        joining_status: candidate.joining_status,
        [field]: newValue,
      };
      if (canSeeFinance) {
        payload.consultancy_charge = candidate.consultancy_charge != null ? candidate.consultancy_charge : 0;
      }
      await axios.put(`${API}/candidates/${candidate.id}`, payload);
      toast.success(`Updated`, { duration: 1200 });
    } catch (err) {
      // Rollback
      setCandidates(prev => prev.map(c => c.id === candidate.id ? { ...c, [field]: original } : c));
      toast.error(err.response?.data?.detail || 'Failed to save');
    }
  }, [canSeeFinance]);


  const formDuplicate = findDuplicate(candidates, formData.candidate_number, formData.company_name, currentCandidate?.id || null);

  const buildPayload = () => {
    const payload = { ...formData };
    payload.consultancy_charge = parseFloat(payload.consultancy_charge) || 0;
    payload.salary = parseFloat(payload.salary) || 0;
    // charge_override: empty string → null (clear override), otherwise number
    if (payload.charge_override === '' || payload.charge_override == null) {
      payload.charge_override = null;
    } else {
      payload.charge_override = parseFloat(payload.charge_override);
    }
    return payload;
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    if (formDuplicate) { toast.error('Duplicate: same mobile number already exists for this company'); return; }
    try {
      await axios.post(`${API}/candidates`, buildPayload());
      toast.success('Candidate added successfully!');
      setIsAddOpen(false);
      resetForm();
      fetchCandidates();
    } catch (error) {
      logError('Error adding candidate:', error);
      toast.error(error.response?.data?.detail || 'Failed to add candidate');
    }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    if (formDuplicate) { toast.error('Duplicate: same mobile number already exists for this company'); return; }
    try {
      // User role: don't send consultancy_charge in edit (backend would also block it)
      const payload = buildPayload();
      if (!canSeeFinance) delete payload.consultancy_charge;
      await axios.put(`${API}/candidates/${currentCandidate.id}`, payload);
      toast.success('Candidate updated successfully!');
      setIsEditOpen(false);
      resetForm();
      fetchCandidates();
    } catch (error) {
      logError('Error updating candidate:', error);
      toast.error(error.response?.data?.detail || 'Failed to update candidate');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this candidate?')) {
      try {
        await axios.delete(`${API}/candidates/${id}`);
        toast.success('Candidate deleted successfully!');
        fetchCandidates();
      } catch (error) {
        logError('Error deleting candidate:', error);
        toast.error(error.response?.data?.detail || 'Failed to delete candidate');
      }
    }
  };

  const openEditDialog = (candidate) => {
    setCurrentCandidate(candidate);
    setFormData({
      interview_date: candidate.interview_date,
      candidate_name: candidate.candidate_name,
      candidate_number: candidate.candidate_number,
      company_name: candidate.company_name,
      date_of_joining: candidate.date_of_joining,
      recruiter_name: candidate.recruiter_name,
      joining_status: candidate.joining_status,
      job_category: candidate.job_category || '',
      salary: candidate.salary != null ? String(candidate.salary) : '',
      consultancy_charge: candidate.consultancy_charge != null ? String(candidate.consultancy_charge) : '',
      charge_override: candidate.charge_override != null ? String(candidate.charge_override) : '',
      override_reason: candidate.override_reason || ''
    });
    setIsEditOpen(true);
  };

  const resetForm = () => {
    setFormData({
      interview_date: '',
      candidate_name: '',
      candidate_number: '',
      company_name: '',
      date_of_joining: '',
      recruiter_name: '',
      joining_status: 'Pending',
      job_category: '',
      salary: '',
      consultancy_charge: '',
      charge_override: '',
      override_reason: ''
    });
    setCurrentCandidate(null);
  };

  const handleExport = async () => {
    try {
      const response = await axios.get(`${API}/candidates/export.xlsx`, {
        params: { month: month || undefined, view: month ? monthView : undefined },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
      const link = document.createElement('a');
      link.href = url;
      const today = new Date().toISOString().split('T')[0];
      link.setAttribute('download', `TulsiJobs_Tracker_${month || today}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Excel exported!');
    } catch (error) {
      logError('Error exporting:', error);
      toast.error('Failed to export data');
    }
  };

  const handleRecruiterExport = async () => {
    try {
      const response = await axios.get(`${API}/reports/recruiters/export.xlsx`, { params: { month }, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `TulsiJobs_Recruiters_${month}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Recruiter report exported!');
    } catch (error) {
      logError('Error exporting recruiter report:', error);
      toast.error('Failed to export recruiter report');
    }
  };

  const isFollowupRequired = (dateOfJoining) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const joiningDate = parseISO(dateOfJoining);
    const daysDiff = differenceInDays(joiningDate, today);
    return daysDiff === 1;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-xl font-bold text-white">Loading...</div>
      </div>
    );
  }

  return (
    <div data-testid="tracker-page">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-4xl sm:text-5xl font-black tracking-tighter text-white mb-2" style={{fontFamily: 'Cabinet Grotesk, sans-serif'}}>
          Candidate Tracker
        </h1>
        <p className="text-base font-medium text-white">Manage and follow up with your candidates</p>
      </div>

      {/* Action Bar */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        {/* Search */}
        <div className="flex-1 relative">
          <MagnifyingGlass size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-black" />
          <Input
            data-testid="search-input"
            type="text"
            placeholder="Search candidates..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 bg-white text-[#0A0A0A] border-2 border-[#0A0A0A] rounded-md px-3 py-2 focus:outline-none focus:ring-0 focus:shadow-[4px_4px_0px_0px_rgba(10,10,10,1)] transition-shadow font-medium"
          />
        </div>

        {/* Actions */}
        <div className="flex gap-2">
          {isOwner && (
            <Button
              data-testid="duplicates-button"
              onClick={() => setIsDupOpen(true)}
              title="Scan for candidates sharing mobile + company"
              className={`${dupScan.scan.total_groups ? 'bg-[#F59E0B]' : 'bg-white'} text-[#0A0A0A] border-2 border-[#0A0A0A] rounded-md font-bold px-4 py-2 shadow-[2px_2px_0px_0px_rgba(10,10,10,1)] hover:-translate-y-0.5 hover:shadow-[4px_4px_0px_0px_rgba(10,10,10,1)] transition-all active:translate-y-0.5 active:shadow-none relative`}
            >
              <Copy size={20} weight="bold" className="mr-2" />
              Duplicates
              {dupScan.scan.total_groups > 0 && (
                <span data-testid="duplicates-badge" className="ml-2 px-1.5 py-0.5 bg-black text-[#FDE047] rounded text-[10px] font-black">{dupScan.scan.total_groups}</span>
              )}
            </Button>
          )}
          <Button
            data-testid="export-button"
            onClick={handleExport}
            className="bg-[#93C5FD] text-[#0A0A0A] border-2 border-[#0A0A0A] rounded-md font-bold px-4 py-2 shadow-[2px_2px_0px_0px_rgba(10,10,10,1)] hover:-translate-y-0.5 hover:shadow-[4px_4px_0px_0px_rgba(10,10,10,1)] transition-all active:translate-y-0.5 active:shadow-none"
          >
            <DownloadSimple size={20} weight="bold" className="mr-2" />
            Export Excel
          </Button>

          <Dialog open={isAddOpen} onOpenChange={setIsAddOpen}>
            <DialogTrigger asChild>
              <Button
                data-testid="add-candidate-button"
                className="bg-[#A3E635] text-[#0A0A0A] border-2 border-[#0A0A0A] rounded-md font-bold px-4 py-2 shadow-[2px_2px_0px_0px_rgba(10,10,10,1)] hover:-translate-y-0.5 hover:shadow-[4px_4px_0px_0px_rgba(10,10,10,1)] transition-all active:translate-y-0.5 active:shadow-none"
              >
                <Plus size={20} weight="bold" className="mr-2" />
                Add Candidate
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-white border-2 border-[#0A0A0A] shadow-[6px_6px_0px_0px_rgba(10,10,10,1)] sm:max-w-[500px]">
              <DialogHeader>
                <DialogTitle className="text-2xl font-bold text-[#0A0A0A]" style={{fontFamily: 'Cabinet Grotesk, sans-serif'}}>Add New Candidate</DialogTitle>
            <DialogDescription className="sr-only">Dialog content</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleAdd} className="space-y-4">
                <div>
                  <Label htmlFor="candidate_name" className="text-xs font-bold uppercase tracking-wider text-[#0A0A0A]">Candidate Name</Label>
                  <Input
                    id="candidate_name"
                    data-testid="input-candidate-name"
                    required
                    value={formData.candidate_name}
                    onChange={(e) => setFormData({...formData, candidate_name: e.target.value})}
                    className="bg-white text-[#0A0A0A] border-2 border-[#0A0A0A] rounded-md px-3 py-2 focus:outline-none focus:ring-0 focus:shadow-[4px_4px_0px_0px_rgba(10,10,10,1)] transition-shadow"
                  />
                </div>
                <div>
                  <Label htmlFor="candidate_number" className="text-xs font-bold uppercase tracking-wider text-[#0A0A0A]">Candidate Number</Label>
                  <Input
                    id="candidate_number"
                    data-testid="input-candidate-number"
                    required
                    value={formData.candidate_number}
                    onChange={(e) => setFormData({...formData, candidate_number: e.target.value})}
                    className="bg-white text-[#0A0A0A] border-2 border-[#0A0A0A] rounded-md px-3 py-2 focus:outline-none focus:ring-0 focus:shadow-[4px_4px_0px_0px_rgba(10,10,10,1)] transition-shadow"
                  />
                </div>
                <CompanyPicker
                  id="company_name"
                  testId="input-company-name"
                  value={formData.company_name}
                  onChange={(v) => setFormData({...formData, company_name: v})}
                  companyNames={companyNames}
                  duplicate={formDuplicate}
                />
                <div>
                  <Label htmlFor="interview_date" className="text-xs font-bold uppercase tracking-wider text-[#0A0A0A]">Interview Date</Label>
                  <Input
                    id="interview_date"
                    data-testid="input-interview-date"
                    type="date"
                    required
                    value={formData.interview_date}
                    onChange={(e) => setFormData({...formData, interview_date: e.target.value})}
                    className="bg-white text-[#0A0A0A] border-2 border-[#0A0A0A] rounded-md px-3 py-2 focus:outline-none focus:ring-0 focus:shadow-[4px_4px_0px_0px_rgba(10,10,10,1)] transition-shadow"
                  />
                </div>
                <div>
                  <Label htmlFor="date_of_joining" className="text-xs font-bold uppercase tracking-wider text-[#0A0A0A]">Date of Joining</Label>
                  <Input
                    id="date_of_joining"
                    data-testid="input-date-joining"
                    type="date"
                    required
                    value={formData.date_of_joining}
                    onChange={(e) => setFormData({...formData, date_of_joining: e.target.value})}
                    className="bg-white text-[#0A0A0A] border-2 border-[#0A0A0A] rounded-md px-3 py-2 focus:outline-none focus:ring-0 focus:shadow-[4px_4px_0px_0px_rgba(10,10,10,1)] transition-shadow"
                  />
                </div>
                <div>
                  <div className="flex items-end justify-between">
                    <Label htmlFor="recruiter_name" className="text-xs font-bold uppercase tracking-wider text-[#0A0A0A]">Recruiter Name</Label>
                    {isOwner && (
                      <button
                        type="button"
                        data-testid="open-manage-recruiters-add"
                        onClick={() => setIsRecruitersOpen(true)}
                        className="text-xs font-bold text-[#0A0A0A] underline hover:text-[#A3E635]"
                      >
                        Manage
                      </button>
                    )}
                  </div>
                  <select
                    id="recruiter_name"
                    data-testid="select-recruiter-name"
                    required
                    value={formData.recruiter_name}
                    onChange={(e) => setFormData({...formData, recruiter_name: e.target.value})}
                    className="w-full bg-white text-[#0A0A0A] border-2 border-[#0A0A0A] rounded-md px-3 py-2 focus:outline-none focus:ring-0 focus:shadow-[4px_4px_0px_0px_rgba(10,10,10,1)] transition-shadow font-medium [&>option]:bg-white [&>option]:text-[#0A0A0A]"
                  >
                    <option value="">Select recruiter…</option>
                    {recruiters.map((r) => (
                      <option key={r.id} value={r.name}>{r.name}</option>
                    ))}
                  </select>
                  {recruiters.length === 0 && (
                    <p className="text-xs text-[#F87171] font-bold mt-1">No recruiters yet — ask the owner to add one via "Manage".</p>
                  )}
                </div>
                <div>
                  <Label htmlFor="joining_status" className="text-xs font-bold uppercase tracking-wider text-[#0A0A0A]">Joining Status</Label>
                  <select
                    id="joining_status"
                    data-testid="select-status"
                    value={formData.joining_status}
                    onChange={(e) => setFormData({...formData, joining_status: e.target.value})}
                    className="w-full bg-white text-[#0A0A0A] border-2 border-[#0A0A0A] rounded-md px-3 py-2 focus:outline-none focus:ring-0 focus:shadow-[4px_4px_0px_0px_rgba(10,10,10,1)] transition-shadow font-medium [&>option]:bg-white [&>option]:text-[#0A0A0A]"
                  >
                    <option value="Pending">Pending</option>
                    <option value="Joined">Joined</option>
                    <option value="Declined">Declined</option>
                    <option value="Rescheduled">Rescheduled</option>
                    <option value="Fired">Fired</option>
                    <option value="Left">Left</option>
                  </select>
                </div>
                <div>
                  <Label htmlFor="job_category" className="text-xs font-bold uppercase tracking-wider text-[#0A0A0A]">Job Category / Position</Label>
                  <Input
                    id="job_category"
                    data-testid="input-job-category"
                    placeholder="e.g. Software Engineer, Sales Executive"
                    value={formData.job_category}
                    onChange={(e) => setFormData({...formData, job_category: e.target.value})}
                    className="bg-white text-[#0A0A0A] border-2 border-[#0A0A0A] rounded-md px-3 py-2 focus:outline-none focus:ring-0 focus:shadow-[4px_4px_0px_0px_rgba(10,10,10,1)] transition-shadow"
                  />
                </div>
                <div>
                  <Label htmlFor="salary" className="text-xs font-bold uppercase tracking-wider text-[#0A0A0A]">Salary Finalized (₹ / month)</Label>
                  <Input
                    id="salary"
                    data-testid="input-salary"
                    type="number"
                    min="0"
                    step="500"
                    placeholder="e.g. 25000"
                    value={formData.salary}
                    onChange={(e) => setFormData({...formData, salary: e.target.value})}
                    className="bg-white text-[#0A0A0A] border-2 border-[#0A0A0A] rounded-md px-3 py-2 focus:outline-none focus:ring-0 focus:shadow-[4px_4px_0px_0px_rgba(10,10,10,1)] transition-shadow"
                  />
                </div>
                <div>
                  <Label htmlFor="consultancy_charge" className="text-xs font-bold uppercase tracking-wider text-[#0A0A0A]">Consultancy Charge (₹)</Label>
                  <Input
                    id="consultancy_charge"
                    data-testid="input-consultancy-charge"
                    type="number"
                    min="0"
                    step="1"
                    placeholder="e.g. 10000"
                    value={formData.consultancy_charge}
                    onChange={(e) => setFormData({...formData, consultancy_charge: e.target.value})}
                    className="bg-white text-[#0A0A0A] border-2 border-[#0A0A0A] rounded-md px-3 py-2 focus:outline-none focus:ring-0 focus:shadow-[4px_4px_0px_0px_rgba(10,10,10,1)] transition-shadow"
                  />
                  {!canSeeFinance && (
                    <p className="text-xs text-[#404040] mt-1 font-medium">Enter the charge once at intake — only the owner can edit this later.</p>
                  )}
                </div>
                <Button
                  type="submit"
                  data-testid="submit-add-candidate"
                  className="w-full bg-[#A3E635] text-[#0A0A0A] border-2 border-[#0A0A0A] rounded-md font-bold px-4 py-2 shadow-[2px_2px_0px_0px_rgba(10,10,10,1)] hover:-translate-y-0.5 hover:shadow-[4px_4px_0px_0px_rgba(10,10,10,1)] transition-all active:translate-y-0.5 active:shadow-none"
                >
                  Add Candidate
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Month navigation */}
      <MonthBar month={month} setMonth={setMonth} monthView={monthView} setMonthView={setMonthView} currentMonth={currentMonth} />
      <MonthSummary candidates={candidates} month={month} isOwner={canSeeFinance} />
      <RecruiterMonthTable candidates={candidates} month={month} onExport={handleRecruiterExport} />

      {/* Date Range Filter */}
      <DateFilterBar
        dateFilterField={dateFilterField}
        setDateFilterField={setDateFilterField}
        dateFrom={dateFrom}
        setDateFrom={setDateFrom}
        dateTo={dateTo}
        setDateTo={setDateTo}
        onClear={clearDateFilter}
        filteredCount={filteredCandidates.length}
        totalCount={candidates.length}
      />

      {/* Inline-edit hint */}
      <div className="mb-2 text-xs text-[#A3E635] font-medium" data-testid="inline-edit-hint">
        💡 Click any cell to edit · Enter to save · Esc to cancel
      </div>

      {/* Table */}
      <div className="bg-[#0F3622] border-2 border-[#A3E635] rounded-md shadow-[4px_4px_0px_0px_rgba(163,230,53,0.3)] overflow-x-auto">
        <table className="w-full tracker-table" data-testid="candidates-table">
          <thead>
            <tr>
              <th>Candidate Name</th>
              <th>Candidate Number</th>
              <th>Company</th>
              <th>Job Category</th>
              <th>Interview Date</th>
              <th>Joining Date</th>
              <th>Recruiter</th>
              <th>Status</th>
              {canSeeFinance && <th>Charge</th>}
              {canSeeFinance && <th>Balance</th>}
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredCandidates.length === 0 ? (
              <tr>
                <td colSpan={canSeeFinance ? 11 : 9} className="text-center py-8 text-white">
                  No candidates found. Add your first candidate!
                </td>
              </tr>
            ) : (
              filteredCandidates.map((candidate) => {
                const needsFollowup = isFollowupRequired(candidate.date_of_joining)
                  && candidate.joining_status.toLowerCase() !== 'joined';
                return (
                  <CandidateRow
                    key={candidate.id}
                    candidate={candidate}
                    month={month}
                    recruiters={recruiters}
                    companySuggestions={companyNames.map(c => c.name)}
                    isOwner={isOwner}
                    canSeeFinance={canSeeFinance}
                    needsFollowup={needsFollowup}
                    inlineSave={inlineSave}
                    onSendAgreement={handleSendAgreement}
                    onPreviewAgreementPdf={handlePreviewAgreementPdf}
                    onDownloadAgreementPdf={handleDownloadAgreementPdf}
                    onOpenEdit={openEditDialog}
                    onMarkJoined={handleMarkJoined}
                    onDelete={handleDelete}
                  />
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Edit Dialog */}
      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent className="bg-white border-2 border-[#0A0A0A] shadow-[6px_6px_0px_0px_rgba(10,10,10,1)] sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-[#0A0A0A]" style={{fontFamily: 'Cabinet Grotesk, sans-serif'}}>Edit Candidate</DialogTitle>
            <DialogDescription className="sr-only">Dialog content</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEdit} className="space-y-4">
            <div>
              <Label htmlFor="edit_candidate_name" className="text-xs font-bold uppercase tracking-wider text-[#0A0A0A]">Candidate Name</Label>
              <Input
                id="edit_candidate_name"
                data-testid="edit-input-candidate-name"
                required
                value={formData.candidate_name}
                onChange={(e) => setFormData({...formData, candidate_name: e.target.value})}
                className="bg-white text-[#0A0A0A] border-2 border-[#0A0A0A] rounded-md px-3 py-2 focus:outline-none focus:ring-0 focus:shadow-[4px_4px_0px_0px_rgba(10,10,10,1)] transition-shadow"
              />
            </div>
            <div>
              <Label htmlFor="edit_candidate_number" className="text-xs font-bold uppercase tracking-wider text-[#0A0A0A]">Candidate Number</Label>
              <Input
                id="edit_candidate_number"
                data-testid="edit-input-candidate-number"
                required
                value={formData.candidate_number}
                onChange={(e) => setFormData({...formData, candidate_number: e.target.value})}
                className="bg-white text-[#0A0A0A] border-2 border-[#0A0A0A] rounded-md px-3 py-2 focus:outline-none focus:ring-0 focus:shadow-[4px_4px_0px_0px_rgba(10,10,10,1)] transition-shadow"
              />
            </div>
            <CompanyPicker
              id="edit_company_name"
              testId="edit-input-company-name"
              value={formData.company_name}
              onChange={(v) => setFormData({...formData, company_name: v})}
              companyNames={companyNames}
              duplicate={formDuplicate}
            />
            <div>
              <Label htmlFor="edit_interview_date" className="text-xs font-bold uppercase tracking-wider text-[#0A0A0A]">Interview Date</Label>
              <Input
                id="edit_interview_date"
                data-testid="edit-input-interview-date"
                type="date"
                required
                value={formData.interview_date}
                onChange={(e) => setFormData({...formData, interview_date: e.target.value})}
                className="bg-white text-[#0A0A0A] border-2 border-[#0A0A0A] rounded-md px-3 py-2 focus:outline-none focus:ring-0 focus:shadow-[4px_4px_0px_0px_rgba(10,10,10,1)] transition-shadow"
              />
            </div>
            <div>
              <Label htmlFor="edit_date_of_joining" className="text-xs font-bold uppercase tracking-wider text-[#0A0A0A]">Date of Joining</Label>
              <Input
                id="edit_date_of_joining"
                data-testid="edit-input-date-joining"
                type="date"
                required
                value={formData.date_of_joining}
                onChange={(e) => setFormData({...formData, date_of_joining: e.target.value})}
                className="bg-white text-[#0A0A0A] border-2 border-[#0A0A0A] rounded-md px-3 py-2 focus:outline-none focus:ring-0 focus:shadow-[4px_4px_0px_0px_rgba(10,10,10,1)] transition-shadow"
              />
            </div>
            <div>
              <div className="flex items-end justify-between">
                <Label htmlFor="edit_recruiter_name" className="text-xs font-bold uppercase tracking-wider text-[#0A0A0A]">Recruiter Name</Label>
                {isOwner && (
                  <button
                    type="button"
                    data-testid="open-manage-recruiters-edit"
                    onClick={() => setIsRecruitersOpen(true)}
                    className="text-xs font-bold text-[#0A0A0A] underline hover:text-[#A3E635]"
                  >
                    Manage
                  </button>
                )}
              </div>
              <select
                id="edit_recruiter_name"
                data-testid="edit-select-recruiter-name"
                required
                value={formData.recruiter_name}
                onChange={(e) => setFormData({...formData, recruiter_name: e.target.value})}
                className="w-full bg-white text-[#0A0A0A] border-2 border-[#0A0A0A] rounded-md px-3 py-2 focus:outline-none focus:ring-0 focus:shadow-[4px_4px_0px_0px_rgba(10,10,10,1)] transition-shadow font-medium [&>option]:bg-white [&>option]:text-[#0A0A0A]"
              >
                <option value="">Select recruiter…</option>
                {recruiters.map((r) => (
                  <option key={r.id} value={r.name}>{r.name}</option>
                ))}
                {/* Preserve legacy free-text value if not present in the list */}
                {formData.recruiter_name && !recruiters.some(r => r.name === formData.recruiter_name) && (
                  <option value={formData.recruiter_name}>{formData.recruiter_name} (legacy)</option>
                )}
              </select>
            </div>
            <div>
              <Label htmlFor="edit_joining_status" className="text-xs font-bold uppercase tracking-wider text-[#0A0A0A]">Joining Status</Label>
              <select
                id="edit_joining_status"
                data-testid="edit-select-status"
                value={formData.joining_status}
                onChange={(e) => setFormData({...formData, joining_status: e.target.value})}
                className="w-full bg-white text-[#0A0A0A] border-2 border-[#0A0A0A] rounded-md px-3 py-2 focus:outline-none focus:ring-0 focus:shadow-[4px_4px_0px_0px_rgba(10,10,10,1)] transition-shadow font-medium [&>option]:bg-white [&>option]:text-[#0A0A0A]"
              >
                <option value="Pending">Pending</option>
                <option value="Joined">Joined</option>
                <option value="Declined">Declined</option>
                <option value="Rescheduled">Rescheduled</option>
                <option value="Fired">Fired</option>
                <option value="Left">Left</option>
              </select>
            </div>
            <div>
              <Label htmlFor="edit_job_category" className="text-xs font-bold uppercase tracking-wider text-[#0A0A0A]">Job Category / Position</Label>
              <Input
                id="edit_job_category"
                data-testid="edit-input-job-category"
                placeholder="e.g. Software Engineer"
                value={formData.job_category}
                onChange={(e) => setFormData({...formData, job_category: e.target.value})}
                className="bg-white text-[#0A0A0A] border-2 border-[#0A0A0A] rounded-md px-3 py-2 focus:outline-none focus:ring-0 focus:shadow-[4px_4px_0px_0px_rgba(10,10,10,1)] transition-shadow"
              />
            </div>
            <div>
              <Label htmlFor="edit_salary" className="text-xs font-bold uppercase tracking-wider text-[#0A0A0A]">Salary Finalized (₹ / month)</Label>
              <Input
                id="edit_salary"
                data-testid="edit-input-salary"
                type="number"
                min="0"
                step="500"
                value={formData.salary}
                onChange={(e) => setFormData({...formData, salary: e.target.value})}
                className="bg-white text-[#0A0A0A] border-2 border-[#0A0A0A] rounded-md px-3 py-2 focus:outline-none focus:ring-0 focus:shadow-[4px_4px_0px_0px_rgba(10,10,10,1)] transition-shadow"
              />
            </div>
            {canSeeFinance && (
              <div>
                <Label htmlFor="edit_consultancy_charge" className="text-xs font-bold uppercase tracking-wider text-[#0A0A0A]">Consultancy Charge (₹)</Label>
                <Input
                  id="edit_consultancy_charge"
                  data-testid="edit-input-consultancy-charge"
                  type="number"
                  min="0"
                  step="1"
                  value={formData.consultancy_charge}
                  onChange={(e) => setFormData({...formData, consultancy_charge: e.target.value})}
                  className="bg-white text-[#0A0A0A] border-2 border-[#0A0A0A] rounded-md px-3 py-2 focus:outline-none focus:ring-0 focus:shadow-[4px_4px_0px_0px_rgba(10,10,10,1)] transition-shadow"
                />
              </div>
            )}
            {canSeeFinance && ['Fired', 'Left'].includes(formData.joining_status) && (
              <div className="bg-[#FEF3C7] border-2 border-[#F59E0B] rounded-md p-3 space-y-3">
                <p className="text-xs font-bold uppercase tracking-wider text-[#92400E]">
                  ⚠ Candidate {formData.joining_status} — Adjust Amount to Collect
                </p>
                <div>
                  <Label htmlFor="edit_charge_override" className="text-xs font-bold uppercase tracking-wider text-[#0A0A0A]">Amount to Collect (₹)</Label>
                  <Input
                    id="edit_charge_override"
                    data-testid="edit-input-charge-override"
                    type="number"
                    min="0"
                    step="1"
                    placeholder="Leave blank to keep original charge"
                    value={formData.charge_override}
                    onChange={(e) => setFormData({...formData, charge_override: e.target.value})}
                    className="bg-white text-[#0A0A0A] border-2 border-[#0A0A0A] rounded-md px-3 py-2 focus:outline-none focus:ring-0 focus:shadow-[4px_4px_0px_0px_rgba(10,10,10,1)] transition-shadow"
                  />
                  <p className="text-xs text-[#92400E] mt-1 font-medium">Set 0 to clear all dues. Leave blank to remove the override.</p>
                </div>
                <div>
                  <Label htmlFor="edit_override_reason" className="text-xs font-bold uppercase tracking-wider text-[#0A0A0A]">Reason / Note</Label>
                  <Input
                    id="edit_override_reason"
                    data-testid="edit-input-override-reason"
                    type="text"
                    placeholder="e.g. Fired in probation, partial refund"
                    value={formData.override_reason}
                    onChange={(e) => setFormData({...formData, override_reason: e.target.value})}
                    className="bg-white text-[#0A0A0A] border-2 border-[#0A0A0A] rounded-md px-3 py-2 focus:outline-none focus:ring-0 focus:shadow-[4px_4px_0px_0px_rgba(10,10,10,1)] transition-shadow"
                  />
                </div>
              </div>
            )}
            <Button
              type="submit"
              data-testid="submit-edit-candidate"
              className="w-full bg-[#A3E635] text-[#0A0A0A] border-2 border-[#0A0A0A] rounded-md font-bold px-4 py-2 shadow-[2px_2px_0px_0px_rgba(10,10,10,1)] hover:-translate-y-0.5 hover:shadow-[4px_4px_0px_0px_rgba(10,10,10,1)] transition-all active:translate-y-0.5 active:shadow-none"
            >
              Update Candidate
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {isOwner && (
        <DuplicatesDialog
          open={isDupOpen}
          onOpenChange={setIsDupOpen}
          scan={dupScan.scan}
          loading={dupScan.loading}
          onRescan={dupScan.run}
          onDeleted={fetchCandidates}
        />
      )}

      {/* Manage Recruiters Dialog (owner only) */}
      <RecruitersDialog
        open={isRecruitersOpen}
        onOpenChange={setIsRecruitersOpen}
        recruiters={recruiters}
        isOwner={isOwner}
        newRecruiter={newRecruiter}
        setNewRecruiter={setNewRecruiter}
        onAddRecruiter={handleAddRecruiter}
        onDeleteRecruiter={handleDeleteRecruiter}
      />
    </div>
  );
};

export default Tracker;